import { isString, isStringEmpty } from "../utils/checkTool";
import request, { AxiosResponseBody } from ".";
import { CommonListParams, CommonStateEnum } from "@/types/common";
import {
  ProductSupplierPrice,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseOrderStateEnum,
  Supplier,
  SupplierAdd,
  SupplierApprovePrice,
  SupplierPrice,
  SupplierPriceAdd,
  SupplierPriceRequestAdd,
} from "@/types/purchase";
import { User, UserAdd } from "@/types/user";
import { Token } from "@/types/token";
import { QuotationRequestItem } from "@/types/quotationRequestItem";
import { Order } from "@/types/inventory";
import { OrderItemV2 } from "@/types/inventoryV2";
const url = "/wms/supplier";

const apis = {
  add: `${url}/add`, // ✅
  get: (id: string) => `${url}/${id}/get`,
  fetch: (id: string) => `${url}/${id}/fetch`, // ✅
  del: (id: string) => `${url}/${id}/del`, // ✅
  disable: (id: string) => `${url}/${id}/disable`, // ✅
  enable: (id: string) => `${url}/${id}/enable`, // ✅
  list: (state?: CommonStateEnum) => `${url}/${state !== undefined ? state + '/' : ''}list`, // ✅
  fetchs: `${url}/fetchs`,
  update: (id: string) => `${url}/${id}/update`, // ✅
  updateByKey: (id: string, key: CustomerUpdateKey) =>
    `${url}/${id}/${key}/update`,

  supplierPrices: (id: string) => `${url}/${id}/price/list`,
  supplierPricesFetch: (id: string) => `${url}/${id}/price/fetchs`,
  supplierPriceHis: (id: string) => id ? `${url}/${id}/price/his` : '/wms/purchase/price/his',
  supplierPriceAdd: (id: string) => `${url}/${id}/price/add`,
  supplierPriceRequestAdd: `/wms/supplierPriceRequest/add`,
  supplierPriceRequestUpdate: (id: string) => `/wms/supplierPriceRequest/${id}/update`,
  supplierPriceRequestFetch: (id: string) => `/wms/supplierPriceRequest/${id}/fetch`,
  supplierUserAdd: (id: string) => `${url}/${id}/user/add`,
  supplierUserList: (id: string) => `${url}/${id}/users`,
  supplierUserDel: (sid: string, uid: string) =>
    `${url}/${sid}/user/${uid}/del`,
  supplierLogin: `${url}/user/login`,
  supplierQuotationRequest: (id: string) => `${url}/${id}/quotationRequest`,
  supplierQuotationsProduct: (sid: string, priceState: SupplierPriceStateEnum) => `${url}/${sid}/product/priceState/${priceState}/list`,
  supplierQuotationsApprove: (sid: string, approveState: SupplierApproveStateEnum) => `${url}/${sid}/priceRequest/approveState/${approveState}/list`,
  supplierTimeOutQuotationRequest: (id: string) => `${url}/${id}/quotationRequest/timeout`,
  supplierPurchaseItems: (id: string, orderState: PurchaseOrderStateEnum) => `${url}/${id}/purchaseItem/${orderState}/list`,
  acceptStateUpdate: (id: string) => `/wms/purchase/order/${id}/acceptState/update`,
  inventoryOrders: (id: string) => `${url}/${id}/inventoryOrders`,
  inventoryOrdersV2: (id: string) => `${url}/${id}/inventoryOrderItems`,
  inventoryOrdersConfirm: (id: string, confirmed: string) => `${url}/${id}/confirmed/${confirmed}/inventoryOrders`,
  inventoryOrdersConfirmV2: (id: string, confirmed: string) => `${url}/${id}/confirmed/${confirmed}/inventoryOrderItems`,

};

type CustomerUpdateKey =
  | "name"
  | "shortName"
  | "address"
  | "zipCode"
  | "email"
  | "contact"
  | "phone"
  | "mobile"
  | "fax"
  | "bank1"
  | "account1"
  | "swiftCode1"
  | "bank2"
  | "account2"
  | "swiftCode2"
  | "currency"
  | "creditLevel"
  | "creditLine"
  | "depositRate"
  | "tradeTerm"
  | "payMethod"
  | "affiliates"
  | "org"
  | "docId"
  | "country"
  | "billingDays"
  | "principal"
  | "taxRate";

export const list = (params?: CommonListParams<string>, state?: CommonStateEnum, wmsApi?: string) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Supplier>, string>(apis.list(state), { params, requestConfig: !!wmsApi ? { baseURL: wmsApi } : undefined });
};

export const fetchs = (params?: CommonListParams<string>) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<Supplier>, string>(apis.fetchs, { params });
};

export const add = (info: SupplierAdd) => {
  return new Promise<Supplier>((resolve, reject) => {
    if (info.code && !isString(info.code)) {
      return reject(10012);
    }
    request<Supplier, SupplierAdd>(apis.add, {
      params: { body: info },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const del = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void, void>(apis.del(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const disable = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void, void>(apis.disable(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const enable = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void, void>(apis.enable(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const get = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return reject(10012);
    }
    return request<Supplier, string>(apis.get(id));
  });
};

export const fetch = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return reject(10012);
    }
    return request<Supplier, string>(apis.fetch(id)).then(res => {
      // @ts-ignore
      resolve(res.body)
    });
  });
};

export interface UpdateByKey {
  key: CustomerUpdateKey;
  value: string;
}

export const updateByKey = (id: string, props: UpdateByKey) => {
  return new Promise<void>((resolve, reject) => {
    if (
      (!isString(id) || isStringEmpty(id)) &&
      props.key &&
      !isString(props.key)
    ) {
      return reject(10012);
    }
    request<void, string>(apis.updateByKey(id, props.key), {
      params: { body: props.value },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const update: (props: SupplierAdd, id: string) => Promise<void> = (
  props,
  id
) => {
  return new Promise((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id) && props.code && !isString(props.code))
    ) {
      return reject(10012);
    }
    request<void, SupplierAdd>(apis.update(id), {
      params: { body: props },
    })
      .then(() => resolve())
      .catch(() => reject());
  });
};

export const supplierPrices: (
  id: string,
  params?: CommonListParams<string>,
  wmsApi?: string
) => Promise<AxiosResponseBody<Array<SupplierPrice>>> = (id, params, wmsApi) => {
  return new Promise((resolve, reject) => {
    request<Array<SupplierPrice>, string>(apis.supplierPrices(id), { params, requestConfig: !!wmsApi ? { baseURL: wmsApi } : undefined })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierPricesFetch: (
  id: string,
  params?: CommonListParams<string>,
  wmsApi?: string
) => Promise<AxiosResponseBody<Array<SupplierPrice>>> = (id, params, wmsApi) => {
  return new Promise((resolve, reject) => {
    request<Array<SupplierPrice>, string>(apis.supplierPricesFetch(id), { params, requestConfig: !!wmsApi ? { baseURL: wmsApi } : undefined })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierPriceHis: (
  id: string,
  isAll: boolean,
  params?: CommonListParams<string>
) => Promise<AxiosResponseBody<Array<SupplierPrice>>> = (id, isAll, params) => {
  return new Promise((resolve, reject) => {
    request<Array<SupplierPrice>, string>(apis.supplierPriceHis(isAll ? '' : id), {
      params,
    })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierPriceRequestAdd = (info: SupplierPriceRequestAdd) => {
  return new Promise<SupplierApprovePrice>((resolve, reject) => {
    request<SupplierApprovePrice, SupplierPriceRequestAdd>(apis.supplierPriceRequestAdd, {
      params: { body: info },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const supplierPriceRequestUpdate = (id: string, info: SupplierPriceRequestAdd) => {
  return new Promise<SupplierApprovePrice>((resolve, reject) => {
    request<SupplierApprovePrice, SupplierPriceRequestAdd>(apis.supplierPriceRequestUpdate(id), {
      params: { body: info },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const supplierPriceRequestFetch = (id: string) => {
  return new Promise<SupplierApprovePrice>((resolve, reject) => {
    request<SupplierApprovePrice, void>(apis.supplierPriceRequestFetch(id))
      .then(res => resolve(res.body))
      .catch(err => reject(err))
  })
};

export const supplierPriceAdd = (id: string, info: SupplierPriceAdd) => {
  return new Promise<SupplierPrice>((resolve, reject) => {
    if (id && !isString(id)) {
      return reject(10012);
    }
    request<SupplierPrice, SupplierPriceAdd>(apis.supplierPriceAdd(id), {
      params: { body: info },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const supplierUsers: (
  id: string,
  params?: CommonListParams<string>
) => Promise<AxiosResponseBody<Array<User>>> = (id, params) => {
  return new Promise((resolve, reject) => {
    request<Array<User>, string>(apis.supplierUserList(id), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierUserAdd = (id: string, user: UserAdd, password: string) => {
  return new Promise<User>((resolve, reject) => {
    if (id && !isString(id)) {
      return reject(10012);
    }
    request<User, UserAdd>(apis.supplierUserAdd(id), {
      params: { body: user, secure: { password } },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const supplierUserDel = (sid: string, uid: string) => {
  return new Promise<void>((resolve, reject) => {
    if (sid && !isString(sid) && uid && !isString(uid)) {
      return reject(10012);
    }
    request<void, void>(apis.supplierUserDel(sid, uid))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const supplierLogin = (mobile: string, password: string) => {
  return new Promise<Token>((resolve, reject) => {
    if (mobile && !isString(mobile) && password && !isString(password)) {
      return reject(10012);
    }
    request<Token, void>(apis.supplierLogin, {
      params: {
        account: { areaCode: "", mobile },
        secure: { password },
      },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const supplierQuotationRequest: (
  sid: string,
  params?: CommonListParams<string>,
) => Promise<AxiosResponseBody<Array<QuotationRequestItem>>> = (sid, params) => {
  return new Promise((resolve, reject) => {
    request<Array<QuotationRequestItem>, string>(apis.supplierQuotationRequest(sid), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierTimeOutQuotationRequest: (
  sid: string,
  params?: CommonListParams<string>,
) => Promise<AxiosResponseBody<Array<QuotationRequestItem>>> = (sid, params) => {
  return new Promise((resolve, reject) => {
    request<Array<QuotationRequestItem>, string>(apis.supplierTimeOutQuotationRequest(sid), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

// 目前PurchaseOrderStateEnum不需要传, 只显示已下单的
export const supplierPurchaseItems = (supplierId: string, params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<PurchaseOrderItem>>>((resolve, reject) => {
    request<Array<PurchaseOrderItem>, string>(apis.supplierPurchaseItems(supplierId, PurchaseOrderStateEnum.PAYED), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};
export const inventoryOrders = (supplierId: string, confirmed: string, params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<Order>>>((resolve, reject) => {
    request<Array<Order>, string>(apis.inventoryOrders(supplierId), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const inventoryOrdersV2 = (supplierId: string, confirmed: string, params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<OrderItemV2>>>((resolve, reject) => {
    request<Array<OrderItemV2>, string>(apis.inventoryOrdersV2(supplierId), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const inventoryOrdersConfirm = (supplierId: string, confirmed: string, params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<Order>>>((resolve, reject) => {
    request<Array<Order>, string>(apis.inventoryOrdersConfirm(supplierId, confirmed), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const inventoryOrdersConfirmV2 = (supplierId: string, confirmed: string, params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<OrderItemV2>>>((resolve, reject) => {
    request<Array<OrderItemV2>, string>(apis.inventoryOrdersConfirmV2(supplierId, confirmed), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};


export const acceptStateUpdate = (id: string, body: number) => {
  return new Promise<PurchaseOrder>((resolve, reject) => {
    request<PurchaseOrder, number>(apis.acceptStateUpdate(id), { params: { body } })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  })
}
export enum SupplierPriceStateEnum {
  ALL = '',
  UN_PRICED = 0,
  PRICED = 1
}
//不传：待审核
export enum SupplierApproveStateEnum {
  ALL = -1,
  REJECTED = 0,
  RESOLVED = 1,
  PENDING = '',
}
export const supplierQuotationsProduct: (
  sid: string,
  priceState: SupplierPriceStateEnum,
  params?: CommonListParams<string>,
) => Promise<AxiosResponseBody<Array<ProductSupplierPrice>>> = (sid, priceState, params) => {
  return new Promise((resolve, reject) => {
    request<Array<ProductSupplierPrice>, string>(apis.supplierQuotationsProduct(sid, priceState), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

export const supplierQuotationsApprove: (
  sid: string,
  approveState: SupplierApproveStateEnum,
  params?: CommonListParams<string>,
) => Promise<AxiosResponseBody<Array<SupplierApprovePrice>>> = (sid, approveState, params) => {
  return new Promise((resolve, reject) => {
    request<Array<SupplierApprovePrice>, string>(apis.supplierQuotationsApprove(sid, approveState), { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const exportObject = {
  list,
  fetchs,
  add,
  del,
  disable,
  enable,
  get,
  fetch,
  updateByKey,
  update,
  supplierPrices,
  supplierPriceAdd,
  supplierPricesFetch,
  supplierUsers,
  supplierUserAdd,
  supplierUserDel,
  supplierLogin,
  supplierQuotationRequest,
  supplierTimeOutQuotationRequest,
  supplierPurchaseItems,
  acceptStateUpdate,
  supplierQuotationsProduct,
  supplierQuotationsApprove,
  supplierPriceRequestAdd,
  inventoryOrders,
  inventoryOrdersV2,
  inventoryOrdersConfirm,
  inventoryOrdersConfirmV2,
};

export type { CustomerUpdateKey };

export default exportObject;
